import { useEffect, useMemo, useRef, useState } from "react";

import { useSearchParams, useNavigate } from "react-router-dom";
import useRealtime from "../../lib/useRealtime";
import ScRequestTypeToggle from "../../components/sc/ScRequestTypeToggle";
import { DURATION_FILTERS, inDurationWindow } from "../../lib/dateRangeFilters";
import {
  Mail, CheckCircle2, Clock, XCircle, Loader2, Wallet,
  ChevronDown, ChevronUp, AlertTriangle, BadgeCheck, HardHat, UserCog, ArrowLeft,
} from "lucide-react";

// Lead-level pills
const STATUS_BADGE = {
  open:      { bg: "bg-sky-50",     fg: "text-sky-700",     label: "Open" },
  accepted:  { bg: "bg-emerald-50", fg: "text-emerald-700",  label: "Accepted" },
  on_hold:   { bg: "bg-amber-50",   fg: "text-amber-700",    label: "On Hold" },
  dropped:   { bg: "bg-red-50",     fg: "text-red-700",      label: "Dropped" },
  converted: { bg: "bg-emerald-50", fg: "text-[#1B5E20]",    label: "Converted" },
};

const OPP_STATUS_BADGE = {
  open:       { bg: "bg-sky-50",     fg: "text-sky-700",   label: "Open" },
  active:     { bg: "bg-sky-50",     fg: "text-sky-700",   label: "Active" },
  won_verbal: { bg: "bg-amber-50",   fg: "text-amber-700", label: "Won · Verbal" },
  won:        { bg: "bg-emerald-50", fg: "text-[#1B5E20]", label: "Won" },
  on_hold:    { bg: "bg-amber-50",   fg: "text-amber-700", label: "On Hold" },
  dropped:    { bg: "bg-red-50",     fg: "text-red-700",   label: "Dropped" },
  lost:       { bg: "bg-red-50",     fg: "text-red-700",   label: "Lost" },
};

const NEGATIVE_STATUSES = new Set(["dropped", "lost", "on_hold"]);

const PAY_BADGE = {
  pending:    { bg: "bg-slate-100",  fg: "text-slate-600",  label: "Pending",    Icon: Clock },
  processing: { bg: "bg-amber-50",   fg: "text-amber-700",  label: "Processing", Icon: Loader2 },
  paid:       { bg: "bg-emerald-50", fg: "text-[#1B5E20]",  label: "Paid",       Icon: CheckCircle2 },
};

// Installation status — derived from Phase-4 "New Installation Service Ticket Logged" activity
const INSTALL_BADGE = {
  not_logged:             { bg: "bg-slate-100",  fg: "text-slate-500",  label: "Not Logged" },
  scheduled:              { bg: "bg-sky-50",     fg: "text-sky-700",    label: "Scheduled" },
  in_progress:            { bg: "bg-amber-50",   fg: "text-amber-700",  label: "In Progress" },
  on_hold:                { bg: "bg-orange-50",  fg: "text-orange-700", label: "On Hold" },
  support_required:       { bg: "bg-rose-50",    fg: "text-rose-700",   label: "Support Req." },
  installation_completed: { bg: "bg-emerald-50", fg: "text-[#1B5E20]",  label: "Installed" },
  closed:                 { bg: "bg-emerald-50", fg: "text-[#1B5E20]",  label: "Closed" },
};

const PHASES = ["Diligence", "Solution", "Regulatory", "Install", "Subsidy"];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ScLeads() {
  const { data: leadsRaw, loading } = useRealtime("/sc/leads");
  const leads = useMemo(() => (Array.isArray(leadsRaw) ? leadsRaw : []), [leadsRaw]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const openTarget = searchParams.get("open");
  const openKind = searchParams.get("kind") || "lead";
  const cameFromDeepLink = !!openTarget;
  const [expanded, setExpanded] = useState(null);
  const [timeFilter, setTimeFilter] = useState("all");
  const rowRefs = useRef({});

  const filtered = useMemo(() => leads.filter((l) => inDurationWindow(l.created_at, timeFilter)), [leads, timeFilter]);

  // Auto-open + scroll to the specific lead/opportunity the user tapped on Home.
  useEffect(() => {
    if (!openTarget || leads.length === 0) return;
    const match = leads.find((l) =>
      openKind === "opportunity" ? l.opp_summary?.id === openTarget : l.id === openTarget
    );
    if (match) {
      setExpanded(match.id);
      const t = setTimeout(() => {
        rowRefs.current[match.id]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [openTarget, openKind, leads]);

  if (loading && leads.length === 0) {
    return <div className="text-xs text-slate-400 mt-10 text-center">Loading your leads…</div>;
  }

  return (
    <div data-testid="sc-leads">
      {cameFromDeepLink && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="sc-leads-back-btn"
          className="mb-3 -mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#1B5E20] hover:text-[#15501A] active:scale-95 transition-transform"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      )}
      {/* Header */}
      <ScRequestTypeToggle active="leads" />
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">New Requests</div>
      <h1 className="text-xl font-bold tracking-tight mt-0.5 text-slate-900">
        {filtered.length}{" "}
        <span className="font-medium text-slate-400">
          {timeFilter === "all" ? "total referred" : DURATION_FILTERS.find((f) => f.v === timeFilter)?.label.toLowerCase()}
        </span>
      </h1>

      {/* Time filter pills */}
      <div className="mt-3 flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="sc-time-filter">
        {DURATION_FILTERS.map((f) => (
          <button
            key={f.v}
            type="button"
            onClick={() => setTimeFilter(f.v)}
            data-testid={`sc-time-${f.v}`}
            className={
              "text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap transition-all border " +
              (timeFilter === f.v
                ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-sm"
                : "bg-white text-slate-600 hover:text-slate-900 border-slate-200")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2.5">
        {filtered.map((l) => {
          const s = STATUS_BADGE[l.status] || STATUS_BADGE.open;
          const op = l.opp_summary;
          const opS = op ? (OPP_STATUS_BADGE[op.status] || OPP_STATUS_BADGE.open) : null;
          const payKey = (op?.sc_payment_status || "pending");
          const pay = op ? (PAY_BADGE[payKey] || PAY_BADGE.pending) : null;
          const PayIcon = pay?.Icon || Clock;
          const isOpen = expanded === l.id;
          const isNegative = !!(op && NEGATIVE_STATUSES.has(op.status));
          const isPaid = op?.sc_payment_status === "paid";
          const isDeepLinkTarget = cameFromDeepLink && isOpen;

          return (
            <div
              key={l.id}
              ref={(el) => { rowRefs.current[l.id] = el; }}
              data-testid={`sc-lead-row-${l.id}`}
              className={
                "relative bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] transition-all border " +
                (isDeepLinkTarget
                  ? "border-amber-400 ring-2 ring-amber-200"
                  : isNegative
                  ? "border-red-200"
                  : isPaid
                    ? "border-emerald-300"
                    : "border-slate-200")
              }
            >
              {isPaid && (
                <div
                  data-testid={`sc-lead-paid-stamp-${l.id}`}
                  className="absolute top-2 right-2 z-10 -rotate-6 select-none pointer-events-none"
                >
                  <div className="border-2 border-[#1B5E20] text-[#1B5E20] bg-white font-bold text-[10px] tracking-[0.15em] px-2 py-0.5 flex items-center gap-1 rounded">
                    <BadgeCheck className="w-3 h-3" />
                    PAID
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : l.id)}
                className="w-full text-left px-4 py-3.5"
                data-testid={`sc-lead-toggle-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3 pr-14">
                  <div className="min-w-0">
                    <div className={"text-[15px] font-semibold truncate " + (isNegative ? "text-red-700" : "text-slate-900")}>
                      {l.customer_snapshot?.customer_name || "—"}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {fmtDate(l.created_at)}{" "}
                      {l.avg_power_consumption ? `· ${l.avg_power_consumption} kWh` : ""}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.bg} ${s.fg}`}>
                      {s.label}
                    </span>
                    {opS && (
                      <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${opS.bg} ${opS.fg}`}>
                        {opS.label}
                      </span>
                    )}
                    {l.is_serviceable === false && (
                      <span
                        data-testid={`sc-lead-not-serviceable-${l.id}`}
                        className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 inline-flex items-center gap-1"
                        title={l.service_check_reason || ""}
                      >
                        <AlertTriangle className="w-2.5 h-2.5" /> Not Serviceable
                      </span>
                    )}
                    {op && op.installation_status && op.installation_status !== "not_logged" && (() => {
                      const ib = INSTALL_BADGE[op.installation_status] || INSTALL_BADGE.not_logged;
                      return (
                        <span
                          data-testid={`sc-lead-install-${l.id}`}
                          className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${ib.bg} ${ib.fg}`}
                        >
                          <HardHat className="w-3 h-3" /> {ib.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {isNegative && op?.status_reason && (
                  <div
                    data-testid={`sc-lead-reason-${l.id}`}
                    className="mt-2.5 flex items-start gap-1.5 bg-red-50 border border-red-200 px-2.5 py-1.5 text-[12px] text-red-700 rounded-lg"
                  >
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-red-500" />
                    <div className="leading-tight">
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-red-500">Reason</span>
                      <div className="mt-0.5 text-red-700">{op.status_reason}</div>
                    </div>
                  </div>
                )}

                {pay && (
                  <div className="mt-2.5 flex items-center justify-between text-[10px]">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${pay.bg} ${pay.fg} font-semibold uppercase tracking-wider`}>
                      <PayIcon className={"w-3 h-3 " + (payKey === "processing" ? "animate-spin" : "")} />
                      {pay.label}
                      {isPaid && (op.sc_payment_amount || 0) > 0 && (
                        <span className="ml-1 font-mono text-[#1B5E20]">· ₹{Number(op.sc_payment_amount).toLocaleString("en-IN")}</span>
                      )}
                    </span>
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                  </div>
                )}
                {!op && (
                  <div className="mt-2.5 flex items-center justify-end">
                    {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                  </div>
                )}
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-slate-50/60" data-testid={`sc-lead-detail-${l.id}`}>
                  <div className="flex items-center gap-3 text-[12px] flex-wrap">
                    {l.customer_snapshot?.email && (
                      <a href={`mailto:${l.customer_snapshot.email}`} className="flex items-center gap-1.5 text-slate-500 hover:text-[#1B5E20] truncate">
                        <Mail className="w-3 h-3" /> {l.customer_snapshot.email}
                      </a>
                    )}
                  </div>

                  {op && (
                    <div>
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Phase progress</div>
                      <div className="mt-1.5 grid grid-cols-5 gap-1.5">
                        {PHASES.map((p, i) => {
                          const phaseNum = i + 1;
                          const done = phaseNum < (op.current_phase || 1);
                          const here = phaseNum === (op.current_phase || 1);
                          return (
                            <div
                              key={p}
                              className={
                                "text-center text-[9px] font-semibold py-1.5 px-1 rounded-lg " +
                                (done ? "bg-[#1B5E20] text-white" : here ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-white text-slate-400 border border-slate-200")
                              }
                            >
                              <div>P{phaseNum}</div>
                              <div className="leading-tight mt-0.5">{p}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {op && (
                    <div data-testid={`sc-lead-install-block-${l.id}`}>
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                        <HardHat className="w-3 h-3" /> Installation
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        {(() => {
                          const ib = INSTALL_BADGE[op.installation_status || "not_logged"] || INSTALL_BADGE.not_logged;
                          return (
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ib.bg} ${ib.fg}`}>
                              {op.installation_status_label || ib.label}
                            </span>
                          );
                        })()}
                        {op.service_incharge?.name && (
                          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                            <UserCog className="w-3 h-3 text-slate-400" />
                            {op.service_incharge.name}
                          </span>
                        )}
                      </div>
                      {op.installation_photos && op.installation_photos.length > 0 && (
                        <div className="mt-2" data-testid={`sc-lead-install-photos-${l.id}`}>
                          <div className="grid grid-cols-4 gap-1.5">
                            {op.installation_photos.slice(0, 4).map((p) => (
                              <a
                                key={p.id}
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="aspect-square bg-slate-100 overflow-hidden border border-slate-200 rounded-md block"
                              >
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.id}`}
                                  alt={p.filename}
                                  loading="lazy"
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ))}
                          </div>
                          {op.installation_photo_count > 4 && (
                            <div className="mt-1 text-[10px] text-slate-400 font-mono">
                              +{op.installation_photo_count - 4} more photos
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {op?.sc_payment_notes && (
                    <div>
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                        <Wallet className="w-3 h-3" /> Payment note
                      </div>
                      <div className="text-[12px] text-slate-700 mt-1 whitespace-pre-wrap">{op.sc_payment_notes}</div>
                    </div>
                  )}
                  {op?.sc_paid_at && (
                    <div className="text-[11px] text-[#1B5E20] font-semibold">Paid on {fmtDate(op.sc_paid_at)}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl">
            <XCircle className="w-8 h-8 mx-auto text-slate-300" />
            <div className="text-sm text-slate-500 mt-3">
              {timeFilter === "all" ? "No leads yet. Tap the + button below to refer one." : "No leads in this time window."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
